import {
	AdverseEventsSchema,
	ComplaintChannelSchema,
	ComplaintSystemSchema,
	DEFAULT_VALUES,
	type AdverseEventContactEntry,
	type AdverseEventsFormValues,
	type ComplaintChannelEntry,
	type ComplaintSystemEntry,
	type AcknowledgementEntry,
} from '#app/routes/mocra+/_form+/adverse-events/form.ts'
import { coerceBoolean } from '#app/routes/mocra+/_form+/adverse-events/helpers.ts'
import {
	ACKNOWLEDGEMENT_CHANNEL_OPTIONS,
	ACKNOWLEDGEMENT_WINDOW_OPTIONS,
} from '#app/routes/mocra+/_form+/intake-options.ts'

const CONTACT_KEYS = [
	'medicalReviewers',
	'seriousEventContacts',
	'followUpContacts',
	'trendAlertContacts',
] as const satisfies readonly (keyof AdverseEventsFormValues)[]

type ContactKey = (typeof CONTACT_KEYS)[number]

type LegacyEscalationKey =
	| 'seriousEventContacts'
	| 'complaintFollowUpContacts'
	| 'trendAlertContacts'

const LEGACY_ESCALATION_MAP: Partial<Record<ContactKey, LegacyEscalationKey>> =
	{
		seriousEventContacts: 'seriousEventContacts',
		followUpContacts: 'complaintFollowUpContacts',
		trendAlertContacts: 'trendAlertContacts',
	}

const CONTACT_DEFAULT_ROLES: Record<ContactKey, string> = {
	medicalReviewers: 'Medical reviewer',
	seriousEventContacts: 'Serious adverse event contact',
	followUpContacts: 'Follow-up contact',
	trendAlertContacts: 'Trend alert contact',
}

function cloneValue<T>(value: T): T {
	if (typeof structuredClone === 'function') {
		return structuredClone(value)
	}
	return JSON.parse(JSON.stringify(value)) as T
}

export function normalizeAdverseEventsDraft(
	value: unknown,
): AdverseEventsFormValues | null {
	if (!value || typeof value !== 'object') return null
	const clone = cloneValue(value) as Record<string, unknown>

	const intakeChannels = normalizeIntakeChannels(clone.intakeChannels)

	const base: AdverseEventsFormValues = {
		intakeChannels,
		complaintSystems: normalizeComplaintSystems(clone.complaintSystems),
		acknowledgement: normalizeAcknowledgement(clone.acknowledgement),
		medicalReviewers: [],
		seriousEventContacts: [],
		followUpContacts: [],
		trendAlertContacts: [],
	}

	const escalation = (clone as { escalation?: unknown }).escalation

	for (const key of CONTACT_KEYS) {
		const defaultRole = CONTACT_DEFAULT_ROLES[key]
		const legacyKey = LEGACY_ESCALATION_MAP[key]
		const candidate =
			clone[key] ??
			(legacyKey
				? getLegacyArray(escalationToRecord(escalation), legacyKey)
				: undefined)
		base[key] = normalizeContactList(candidate, defaultRole)
	}

	const parsed = AdverseEventsSchema.safeParse(base)
	if (parsed.success) {
		return ensureContactCollections(parsed.data)
	}
	return ensureContactCollections(base)
}

function ensureContactCollections(
	value: AdverseEventsFormValues,
): AdverseEventsFormValues {
	value.complaintSystems = normalizeComplaintSystems(value.complaintSystems)
	for (const key of CONTACT_KEYS) {
		const defaultRole = CONTACT_DEFAULT_ROLES[key]
		value[key] = normalizeContactList(value[key], defaultRole)
	}
	return value
}

function normalizeIntakeChannels(input: unknown): ComplaintChannelEntry[] {
	if (!Array.isArray(input)) return []
	const entries: ComplaintChannelEntry[] = []
	for (const candidate of input) {
		const parsed = ComplaintChannelSchema.safeParse(candidate)
		if (parsed.success) {
			entries.push(parsed.data)
		}
	}
	return entries
}

function normalizeContactList(
	input: unknown,
	defaultRole: string,
): AdverseEventContactEntry[] {
	if (!Array.isArray(input)) return []
	return input
		.map((entry) => normalizeContactEntry(entry, defaultRole))
		.filter((entry): entry is AdverseEventContactEntry => entry !== null)
}

function normalizeContactEntry(
	input: unknown,
	defaultRole: string,
): AdverseEventContactEntry | null {
	if (!input || typeof input !== 'object') return null
	const raw = input as Record<string, unknown>

	const role = toOptionalString(raw.role) ?? defaultRole
	const name = toOptionalString(raw.name) ?? toOptionalString(raw.fallbackName)
	if (!name) return null
	const email = toOptionalEmail(raw.email) ?? toOptionalEmail(raw.fallbackEmail)
	if (!email) return null

	const phone = toOptionalString(raw.phone)
	const contactId = toOptionalString(raw.contactId)
	const fallbackName = toOptionalString(raw.fallbackName) ?? name
	const fallbackEmail = toOptionalEmail(raw.fallbackEmail) ?? email
	const fallbackTitle = toOptionalString(raw.fallbackTitle) ?? role
	const fallbackPhone = toOptionalString(raw.fallbackPhone) ?? phone

	return {
		role,
		name,
		email,
		phone,
		contactId,
		fallbackName,
		fallbackEmail,
		fallbackTitle,
		fallbackPhone,
	}
}

function escalationToRecord(value: unknown): Record<string, unknown> | null {
	if (!value || typeof value !== 'object') return null
	return value as Record<string, unknown>
}

function getLegacyArray(
	escalation: Record<string, unknown> | null,
	legacyKey: LegacyEscalationKey,
): unknown {
	if (!escalation) return undefined
	return escalation[legacyKey]
}

function toOptionalString(value: unknown): string | undefined {
	if (typeof value !== 'string') return undefined
	const trimmed = value.trim()
	return trimmed.length > 0 ? trimmed : undefined
}

function toOptionalEmail(value: unknown): string | undefined {
	const trimmed = toOptionalString(value)
	if (!trimmed) return undefined
	return trimmed
}

function normalizeComplaintSystems(input: unknown): ComplaintSystemEntry[] {
	if (!Array.isArray(input)) return []
	const deduped: ComplaintSystemEntry[] = []
	const seen = new Set<string>()

	for (const candidate of input) {
		if (!candidate || typeof candidate !== 'object') continue
		const parsed = ComplaintSystemSchema.safeParse(candidate)
		if (!parsed.success) continue

		const name = parsed.data.name.trim()
		if (!name) continue

		const usage = Array.from(new Set(parsed.data.usage.map((value) => value)))

		if (usage.length === 0) continue

		const entry: ComplaintSystemEntry = {
			platform: parsed.data.platform,
			name,
			usage,
		}
		const key = `${entry.platform}|${name.toLowerCase()}|${usage
			.slice()
			.sort()
			.join(',')}`
		if (seen.has(key)) continue
		seen.add(key)
		deduped.push(entry)
	}

	return deduped
}

const ACKNOWLEDGEMENT_WINDOW_VALUE_SET = new Set(
	ACKNOWLEDGEMENT_WINDOW_OPTIONS.map((option) => option.value),
)

const ACKNOWLEDGEMENT_CHANNEL_VALUE_SET = new Set(
	ACKNOWLEDGEMENT_CHANNEL_OPTIONS.map((option) => option.value),
)

function normalizeAcknowledgement(input: unknown): AcknowledgementEntry {
	const defaults = cloneValue(DEFAULT_VALUES.acknowledgement)
	if (!input || typeof input !== 'object') {
		return defaults
	}
	const raw = input as Record<string, unknown>
	const windowValue = resolveAcknowledgementWindow(raw.window)
	const channelValue = resolveAcknowledgementChannel(raw.channel)
	const coversWeekends = coerceBoolean(raw.coversWeekends)
	const customWindowRaw =
		typeof raw.customWindow === 'string' ? raw.customWindow.trim() : ''
	const customWindow =
		windowValue === 'custom' ? customWindowRaw : defaults.customWindow

	return {
		window: windowValue,
		channel: channelValue,
		coversWeekends,
		customWindow,
	}
}

function resolveAcknowledgementWindow(
	value: unknown,
): AcknowledgementEntry['window'] {
	if (
		typeof value === 'string' &&
		ACKNOWLEDGEMENT_WINDOW_VALUE_SET.has(value)
	) {
		return value
	}
	return DEFAULT_VALUES.acknowledgement.window
}

function resolveAcknowledgementChannel(
	value: unknown,
): AcknowledgementEntry['channel'] {
	if (
		typeof value === 'string' &&
		ACKNOWLEDGEMENT_CHANNEL_VALUE_SET.has(value)
	) {
		return value
	}
	return DEFAULT_VALUES.acknowledgement.channel
}
